import { jwtDecode } from "jwt-decode";
import React, { createContext, useEffect, useState } from "react";
import { UserInfo } from "../models/UserInfo";
import { useOrganization } from "./OrganizationContext";
import {UserRole} from "../models/User";
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../constants/AuthStorageKeys";

export interface JwtPayload {
  exp?: number;
  id?: string;
  email?: string;
  username?: string;
  roles: string | string[];
  managed_organizations?: string;
  temporary_access_organizations?: string;
}

interface AuthContextProps {
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  hasAccessToOrganization: boolean;
  getDecodedToken: () => JwtPayload | null;
  login: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  getUserId: () => string | null;
  getUserInfo: () => UserInfo;
  hasManagedAccessToOrganization: (organizationId: string) => boolean;
  hasTemporaryAccessToOrganizationDocuments: (organizationId: string) => boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { setSelectedOrganizationId, selectedOrganizationId } = useOrganization();
  const [accessToken, setAccessToken] = useState<string | null>(() => localStorage.getItem(ACCESS_TOKEN_KEY),);
  const [refreshToken, setRefreshToken] = useState<string | null>(() => localStorage.getItem(REFRESH_TOKEN_KEY),);
  const isAuthenticated = !!accessToken;

  const login = (accessToken: string, refreshToken: string) => {
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
  };

  const logout = () => {
    setSelectedOrganizationId(null);
    setAccessToken(null);
    setRefreshToken(null);
  };

  const getDecodedToken = (): JwtPayload | null => {
    if (!accessToken) return null;
    try {
      return jwtDecode<JwtPayload>(accessToken);
    } catch (error) {
      console.error("Error decoding token", error);
      return null;
    }
  };

  const getUserId = (): string | null => {
    const decodedToken = getDecodedToken();
    return decodedToken?.id || null;
  };

  const getUserInfo = (): UserInfo => {
    const decodedToken = getDecodedToken();
    const role = decodedToken?.roles.toString().toLowerCase() || '';
    return {
      role: role,
      emailAddress: decodedToken?.email || '',
      userName: decodedToken?.username || '',
      isAdmin: () => role === UserRole.Admin,
      isEditor: () => role === UserRole.Editor,
      isViewer: () => role === UserRole.Viewer,
    };
  };

  const hasManagedAccessToOrganization = (organizationId: string): boolean => {
    const decodedToken = getDecodedToken();
    const role = decodedToken?.roles.toString().toLowerCase() || '';
    if (role === UserRole.Admin)
      return true;
    
    const managedOrganizations = decodedToken?.managed_organizations;
    if (!managedOrganizations)
      return false;

    return managedOrganizations.split(",").includes(organizationId);
  };

  const hasTemporaryAccessToOrganizationDocuments = (organizationId: string): boolean => {
    const decodedToken = getDecodedToken();
    const role = decodedToken?.roles.toString().toLowerCase() || '';
    if (role === UserRole.Admin)
      return true;

    const claim: string | undefined = decodedToken?.temporary_access_organizations;
    if (!claim)
      return false;

    const now = new Date();
    const accessList: { orgId: string; endDate: Date }[] = claim
      .split(",")
      .map((entry) => {
        const [orgId, endDateStr] = entry.split(";");
        return {
          orgId,
          endDate: new Date(endDateStr),
        };
      });

    for (let i = 0; i < accessList.length; i++) {
      const access = accessList[i];
      if (access.orgId === organizationId && access.endDate > now) {
        return true;
      }
    }

    return false;
  };

  const hasAccessToOrganization =  hasManagedAccessToOrganization(selectedOrganizationId ?? "") || hasTemporaryAccessToOrganizationDocuments(selectedOrganizationId ?? "");
  
  useEffect(() => {
    const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (storedAccessToken && storedRefreshToken) {
      const decodedToken = jwtDecode<JwtPayload>(storedAccessToken);
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp < currentTime) {
        logout();
      } else {
        login(storedAccessToken, storedRefreshToken);
      }
    }
  }, []);

  useEffect(() => {
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  }, [accessToken]);

  useEffect(() => {
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }, [refreshToken]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        refreshToken,
        isAuthenticated,
        hasAccessToOrganization,
        getDecodedToken,
        login,
        logout,
        getUserId,
        getUserInfo,
        hasManagedAccessToOrganization,
        hasTemporaryAccessToOrganizationDocuments,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = React.useContext(AuthContext);
  if (!context)
    throw new Error("useAuth must be used within an AuthProvider");
  
  return context;
};
